/**
 * Extracts a single frame from a video URL as a JPEG data URL.
 *
 * Two stages, so the visible player is never touched and screenshots stay
 * reliable:
 *
 *  1. Fast path — draw from a detached <video crossOrigin="anonymous">. This
 *     works whenever the browser performs a clean CORS fetch for our element.
 *  2. Fallback — when that canvas comes back tainted (Chrome reuses the bytes
 *     the *player's* no-crossOrigin element already cached for the same signed
 *     URL, so toDataURL throws a SecurityError), re-fetch the video ourselves
 *     in CORS mode and hand the capture element a same-origin blob: URL, which
 *     can never taint the canvas. This is the case behind the intermittent
 *     "screenshot unavailable" — the fast path taints depending on the
 *     player's cache state; the blob path is deterministic.
 *
 * The media bucket allows the CRM origins for GET/HEAD, so both stages get
 * CORS headers on prod/staging; on any origin or source without them the
 * capture fails softly and the caller sends feedback without a screenshot.
 */

const CAPTURE_TIMEOUT_MS = 10_000;
const FETCH_TIMEOUT_MS = 20_000;
const MAX_FRAME_WIDTH = 960;
const JPEG_QUALITY = 0.72;
const MAX_VIDEO_FETCH_BYTES = 64 * 1024 * 1024;

export async function captureVideoFrame(
  src: string,
  atSeconds: number,
  signal?: AbortSignal,
): Promise<string | null> {
  if (signal?.aborted) return null;
  const direct = await captureFromElement(src, atSeconds, true, signal);
  if (direct) return direct;
  if (signal?.aborted) return null;
  return captureViaBlob(src, atSeconds, signal);
}

function captureFromElement(
  src: string,
  atSeconds: number,
  useCors: boolean,
  signal?: AbortSignal,
): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    let settled = false;

    const finish = (result: string | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      video.removeAttribute("src");
      video.load();
      resolve(result);
    };

    const onAbort = () => finish(null);

    const timer = setTimeout(() => finish(null), CAPTURE_TIMEOUT_MS);
    if (signal?.aborted) {
      finish(null);
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });

    // A blob: URL is same-origin and never taints the canvas, so the fallback
    // path deliberately omits crossOrigin (setting it can make some browsers
    // refuse the object URL).
    if (useCors) video.crossOrigin = "anonymous";
    video.muted = true;
    video.preload = "auto";
    video.playsInline = true;

    video.addEventListener("error", () => finish(null));
    video.addEventListener("loadedmetadata", () => {
      try {
        const duration = Number.isFinite(video.duration)
          ? video.duration
          : atSeconds;
        video.currentTime = Math.min(Math.max(0, atSeconds), duration);
      } catch {
        finish(null);
      }
    });
    video.addEventListener("seeked", () => {
      try {
        const scale = Math.min(
          1,
          MAX_FRAME_WIDTH / (video.videoWidth || MAX_FRAME_WIDTH),
        );
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        if (!canvas.width || !canvas.height) {
          finish(null);
          return;
        }
        const context = canvas.getContext("2d");
        if (!context) {
          finish(null);
          return;
        }
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        // toDataURL throws a SecurityError when the canvas is tainted — the
        // signal the fast path failed and the blob fallback should run.
        finish(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      } catch {
        finish(null);
      }
    });

    video.src = src;
  });
}

async function captureViaBlob(
  src: string,
  atSeconds: number,
  signal?: AbortSignal,
): Promise<string | null> {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort, { once: true });
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  let objectUrl: string | null = null;
  try {
    const response = await fetch(src, {
      mode: "cors",
      credentials: "omit",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const declaredBytes = Number(response.headers.get("content-length"));
    if (
      Number.isFinite(declaredBytes) &&
      declaredBytes > MAX_VIDEO_FETCH_BYTES
    ) {
      return null;
    }
    const blob = await readBoundedBlob(response, MAX_VIDEO_FETCH_BYTES);
    objectUrl = URL.createObjectURL(blob);
    return await captureFromElement(objectUrl, atSeconds, false, signal);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}

async function readBoundedBlob(response: Response, maxBytes: number) {
  if (!response.body) return response.blob();
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel("video exceeds capture fallback limit");
      throw new Error("video exceeds capture fallback limit");
    }
    chunks.push(value);
  }
  return new Blob(chunks, {
    type: response.headers.get("content-type") || "video/mp4",
  });
}
